import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Divided Attention — FTUE demo (3 essais progressive intro).
 *
 * Spec : wiki/games/divided-attention.md §FTUE
 *   E1 : centre seul (1500 ms). Forme à identifier.
 *   E2 : périphérie seule (1500 ms). Position à pointer.
 *   E3 : centre + périphérie simultanés (1500 ms).
 *
 * Pas de scoring — pédagogique uniquement.
 *
 * Voice constraint : strings sober, aucune mention archetype/lore.
 */
import { useState } from 'react';
import { CENTRAL_LABELS, eccentricityRadiusPx, } from './params';
const RADIUS = eccentricityRadiusPx['10deg'];
const DEMO_STEPS = [
    {
        idx: 1,
        title: 'Étape 1 : centre seul',
        hint: 'Une forme apparaît au centre. Tu l’identifieras après le flash.',
        central: 'square',
        peripheralAngle: null,
        durationMs: 1500,
    },
    {
        idx: 2,
        title: 'Étape 2 : périphérie seule',
        hint: 'Un point flashe à un angle. Tu pointeras où il a été vu.',
        central: null,
        peripheralAngle: 90,
        durationMs: 1500,
    },
    {
        idx: 3,
        title: 'Étape 3 : les deux ensemble',
        hint: 'Centre et périphérie en même temps. C’est l’attention divisée.',
        central: 'triangle',
        peripheralAngle: 270,
        durationMs: 1500,
    },
];
export function Demo({ level: _level, onComplete, onSkip }) {
    const [stepIdx, setStepIdx] = useState(0);
    const [showStimulus, setShowStimulus] = useState(true);
    const step = DEMO_STEPS[stepIdx];
    const isLast = stepIdx === DEMO_STEPS.length - 1;
    const handleNext = () => {
        if (isLast) {
            onComplete();
            return;
        }
        setStepIdx((i) => i + 1);
        setShowStimulus(true);
    };
    return (_jsxs("div", { className: "flex flex-1 flex-col items-center gap-s4 py-s4 text-center", children: [_jsx("p", { className: "font-mono text-[9.5px] uppercase tracking-[0.30em] text-cyan-text-faint", children: "D\u00E9mo guid\u00E9e" }), _jsx("h2", { className: "font-display text-[22px] font-medium leading-7 tracking-cy-display text-cyan-text", children: step.title }), _jsx("p", { className: "max-w-[320px] text-[13.5px] text-cyan-text-secondary", children: step.hint }), _jsxs("div", { className: "relative mx-auto h-[280px] w-[280px] max-w-full", children: [step.central !== null && (_jsx("div", { className: [
                            'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2',
                            'flex h-[36px] w-[36px] items-center justify-center text-[34px] leading-none text-cyan-text',
                            showStimulus ? 'opacity-100' : 'opacity-30',
                            'transition-opacity duration-feedback',
                        ].join(' '), children: CENTRAL_LABELS[step.central] })), step.peripheralAngle !== null && (_jsx("div", { className: [
                            'absolute left-1/2 top-1/2 h-[14px] w-[14px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-cyan-network',
                            showStimulus ? 'opacity-100' : 'opacity-30',
                            'transition-opacity duration-feedback',
                        ].join(' '), style: {
                            transform: `translate(-50%, -50%) rotate(${step.peripheralAngle}deg) translateY(-${RADIUS}px) rotate(-${step.peripheralAngle}deg)`,
                        } }))] }), _jsxs("div", { className: "grid w-full grid-cols-2 gap-s3 text-start text-[12px] text-cyan-text-secondary", children: [_jsxs("div", { className: "border border-cyan-border-faint bg-cyan-surface p-s3", children: [_jsx("span", { className: "block font-mono uppercase tracking-[0.20em] text-cyan-text-faint", children: "\u00C0 traiter" }), _jsx("span", { className: "text-[13px] text-cyan-text", children: step.central !== null && step.peripheralAngle !== null
                                    ? 'Centre et périphérie'
                                    : step.central !== null
                                        ? 'Centre seul'
                                        : 'Périphérie seule' })] }), _jsxs("div", { className: "border border-cyan-border-faint bg-cyan-surface p-s3", children: [_jsx("span", { className: "block font-mono uppercase tracking-[0.20em] text-cyan-text-faint", children: "Dur\u00E9e du flash" }), _jsxs("span", { className: "text-[13px] text-cyan-text", children: [step.durationMs, " ms"] })] })] }), _jsxs("div", { className: "flex flex-col items-stretch gap-s3 self-stretch", children: [_jsx("button", { type: "button", onClick: () => setShowStimulus((v) => !v), className: "border border-cyan-border-faint px-s4 py-s2 text-[12px] text-cyan-text-secondary hover:border-cyan-border", children: showStimulus ? 'Masquer le stimulus' : 'Réafficher le stimulus' }), _jsx("button", { type: "button", onClick: handleNext, className: "inline-flex h-12 w-full items-center justify-center bg-cyan-core font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-surface transition active:scale-[0.985] hover:bg-cyan-network", children: isLast ? 'Démarrer la session' : 'Étape suivante' }), _jsx("button", { type: "button", onClick: onSkip, className: "text-[12px] text-cyan-text-secondary underline-offset-4 hover:underline", children: "Passer la d\u00E9mo" })] })] }));
}
//# sourceMappingURL=demo.js.map